import { useEffect, useState } from "react";
import { toast } from "sonner";
import API from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../ui/dialog";
import { TenantPicker } from "./TenantPicker";
import { money, thisMonth, monthLabel } from "../../../lib/bespoke";

const nextMonth = () => { const d = new Date(); d.setUTCMonth(d.getUTCMonth() + 1, 1); return d.toISOString().slice(0, 7); };

/** Switch a module on for a tenant with its one-off + monthly fees (billed by invoice). */
export function AddTenantDialog({ open, onOpenChange, module, tenant: fixedTenant, onAdded }) {
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({ one_off_fee: "", monthly_fee: "", monthly_from: thisMonth(), note: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTenant(fixedTenant || null);
    setForm({ one_off_fee: module?.one_off_fee ?? "", monthly_fee: module?.monthly_fee ?? "", monthly_from: thisMonth(), note: "" });
  }, [open, module, fixedTenant]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const oneOff = Number(form.one_off_fee || 0);
  const monthly = Number(form.monthly_fee || 0);

  const submit = async () => {
    if (!tenant) return toast.error("Pick a tenant");
    setBusy(true);
    try {
      const { data } = await API.post(`/platform/bespoke/modules/${module.id}/tenants`, { tenant_id: tenant.id, one_off_fee: oneOff, monthly_fee: monthly, monthly_from: form.monthly_from, note: form.note });
      toast.success(`${module.name} added to ${tenant.name}`);
      onAdded?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add the module");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bespoke-add-tenant-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">Add {module?.name} to a tenant</DialogTitle>
          <DialogDescription>The module switches on straight away{module?.status !== "live" ? " (it shows in their menu once it goes live)" : ""}. Fees are billed by invoice — the charges appear under Billing for you to mark paid.</DialogDescription>
        </DialogHeader>
        {!fixedTenant && <TenantPicker value={tenant} onChange={setTenant} excludeModuleId={module?.id} />}
        {tenant && <p className="text-xs rounded-md bg-muted px-3 py-2" data-testid="bespoke-add-tenant-selected">Adding to <b>{tenant.name}</b> (/{tenant.slug})</p>}
        <div className="grid gap-3 sm:grid-cols-2 mt-1">
          <div><Label className="text-xs">One-off set-up fee (£)</Label><Input type="number" min="0" step="0.01" value={form.one_off_fee} onChange={set("one_off_fee")} className="mt-1" data-testid="bespoke-add-oneoff-input" /></div>
          <div><Label className="text-xs">Monthly fee (£)</Label><Input type="number" min="0" step="0.01" value={form.monthly_fee} onChange={set("monthly_fee")} className="mt-1" data-testid="bespoke-add-monthly-input" /></div>
          {monthly > 0 && (
            <div className="sm:col-span-2">
              <Label className="text-xs">First monthly charge</Label>
              <div className="flex gap-2 mt-1">
                {[thisMonth(), nextMonth()].map((ym) => (
                  <button key={ym} type="button" onClick={() => setForm((f) => ({ ...f, monthly_from: ym }))}
                    className={`flex-1 rounded-md border px-3 py-2 text-xs transition-colors ${form.monthly_from === ym ? "border-primary bg-primary/10 text-primary font-medium" : "border-border hover:bg-muted"}`} data-testid={`bespoke-add-from-${ym === thisMonth() ? "this" : "next"}`}>
                    {monthLabel(ym)}{ym === thisMonth() ? " (this month)" : " (next month)"}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="sm:col-span-2"><Label className="text-xs">Note (internal)</Label><Input value={form.note} onChange={set("note")} maxLength={500} placeholder="e.g. agreed with Dave on the phone 3 Nov" className="mt-1" data-testid="bespoke-add-note-input" /></div>
        </div>
        <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground" data-testid="bespoke-add-summary">
          {oneOff > 0 && <p>Raises a <b>{money(oneOff)}</b> set-up charge now (14-day terms).</p>}
          {monthly > 0 && <p>Then <b>{money(monthly)}</b> every month from {monthLabel(form.monthly_from)} — each month's charge is raised automatically on the 1st.</p>}
          {oneOff === 0 && monthly === 0 && <p>No fees — the module is complimentary for this tenant.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !tenant} data-testid="bespoke-add-tenant-submit">{busy ? "Adding…" : "Switch on for this tenant"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
